/**
 * Paper Atlas · SVG primitives (Cycle 14 · Feb 2026)
 * ───────────────────────────────────────────────────────────
 * Composable building blocks for the "explorer's notebook"
 * design language. All SVG, all reusable, all colour-driven
 * by design tokens — no raster art, no clip-art, no gradients.
 *
 * Exports:
 *   <GrainOverlay />   — 4 % noise texture layered above cards / hero
 *   <TornEdge  />      — SVG-clip path for a torn-paper card edge
 *   <PaperCard />      — parchment card wrapper (border + stamp shadow)
 *   <WaxSeal   />      — circular sticker with initials, printed keepsake feel
 *   <Stamp     />      — passport stamp (angled rect + label + date)
 *   <InkRoute  />      — dotted travel route between two points
 *   <Compass   />      — small compass rose (16 or 24 px)
 *
 * Every primitive:
 *  • Accepts `className` for utility overrides
 *  • Renders `aria-hidden` by default (decorative)
 *  • Passes `title` through as SVG <title> when provided (a11y)
 *  • Uses currentColor for the ink stroke so the parent can theme it
 */

import React from "react";

// ─── GrainOverlay ───────────────────────────────────────────
// Subtle noise texture — layer ABOVE content with pointer-events:none.
// Uses SVG turbulence so no raster download; ~1 KB inline.
export function GrainOverlay({ opacity = 0.06, className = "" }) {
  return (
    <svg
      aria-hidden
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ opacity, mixBlendMode: "multiply" }}
    >
      <filter id="paper-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7" />
        <feColorMatrix values="0 0 0 0 0.24  0 0 0 0 0.20  0 0 0 0 0.13  0 0 0 1 0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#paper-grain)" />
    </svg>
  );
}

// ─── TornEdge ───────────────────────────────────────────────
// Renders a torn top OR bottom edge as an inline SVG mask, sized to
// its container width. Height default = 12 px. Consumers place it
// inside a relative parent so it clips the paper card.
export function TornEdge({ side = "bottom", color = "#F4EAD0", className = "" }) {
  // 24 zig peaks generated deterministically — same shape every render.
  const peaks = 24;
  const step = 100 / peaks; // percent
  let d = `M 0 ${side === "top" ? "12" : "0"} `;
  for (let i = 0; i <= peaks; i++) {
    const x = i * step;
    const dip = (i % 2 === 0 ? 0 : 6) + (i % 3 === 0 ? 2 : 0); // subtle irregularity
    if (side === "top") {
      d += `L ${x} ${dip} `;
    } else {
      d += `L ${x} ${12 - dip} `;
    }
  }
  d += `L 100 ${side === "top" ? "12" : "0"} L 0 ${side === "top" ? "12" : "0"} Z`;
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 12"
      preserveAspectRatio="none"
      className={`absolute left-0 right-0 h-3 w-full pointer-events-none ${side === "top" ? "-top-[1px]" : "-bottom-[1px]"} ${className}`}
    >
      <path d={d} fill={color} />
    </svg>
  );
}

// ─── PaperCard ──────────────────────────────────────────────
// The canonical Paper Atlas card wrapper. Warm parchment fill,
// ink border, hard offset shadow, optional grain overlay + torn edge.
export function PaperCard({
  children, className = "", grain = true, torn = false,
  as: Tag = "div", testId,
}) {
  return (
    <Tag
      data-testid={testId}
      className={
        // Cycle 16 · Luminous World — swap fill + border for night
        // tokens under `.dark`. Grain overlay stays (works on both).
        "relative bg-paper-100 dark:bg-night-900 border-[3px] border-slate-900 dark:border-starlight-200 rounded-xl shadow-stamp dark:shadow-[0_3px_0_0_#0A0F1F] " +
        className
      }
    >
      {grain && <GrainOverlay />}
      <div className="relative z-10">{children}</div>
      {torn && <TornEdge side="bottom" color="#F4EAD0" />}
    </Tag>
  );
}

// ─── WaxSeal ────────────────────────────────────────────────
// Circular sticker for level-up/mastery keepsakes. `initials` = 1-2
// chars. Diameter defaults to 64 px.
export function WaxSeal({ initials = "★", size = 64, color = "#B4472A", label }) {
  const r = size / 2;
  return (
    <svg
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={!label}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="inline-block drop-shadow-[0_2px_0_rgba(15,23,42,0.9)]"
    >
      {label && <title>{label}</title>}
      {/* Scalloped rim — 20 small notches around the seal */}
      {Array.from({ length: 20 }).map((_, i) => {
        const angle = (i / 20) * Math.PI * 2;
        const cx = r + Math.cos(angle) * (r - 3);
        const cy = r + Math.sin(angle) * (r - 3);
        return <circle key={i} cx={cx} cy={cy} r={3.5} fill={color} />;
      })}
      <circle cx={r} cy={r} r={r - 6} fill={color} stroke="#0F172A" strokeWidth={2.5} />
      <text
        x={r}
        y={r + size * 0.06}
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="Nunito, system-ui"
        fontWeight={700}
        fontSize={size * 0.42}
        fill="#FBF6E8"
        style={{ letterSpacing: "-0.02em" }}
      >
        {initials}
      </text>
    </svg>
  );
}

// ─── Stamp ──────────────────────────────────────────────────
// Angled passport stamp with country/topic label + date. Used for
// completions, streaks, and personal-best moments.
export function Stamp({
  label = "EXPLORER", date, angle = -6, tint = "#4E8A55",
  className = "",
}) {
  return (
    <div
      className={`inline-flex items-center justify-center px-3 py-1.5 rounded-md border-[2.5px] font-heading font-bold text-[10px] tracking-[0.15em] uppercase ${className}`}
      style={{
        transform: `rotate(${angle}deg)`,
        color: tint,
        borderColor: tint,
        backgroundColor: "rgba(251, 246, 232, 0.35)",
        letterSpacing: "0.18em",
      }}
    >
      <span aria-hidden className="mr-1.5 text-[13px] leading-none">✈︎</span>
      <span>{label}</span>
      {date && <span className="ml-1.5 opacity-70">· {date}</span>}
    </div>
  );
}

// ─── InkRoute ───────────────────────────────────────────────
// Dotted travel line between two points (SVG). Used on the passport,
// on progress trails, and on the sky-map hero.
export function InkRoute({ from = [8, 20], to = [180, 20], color = "#4A6FA5", dashed = true }) {
  return (
    <svg
      aria-hidden
      className="overflow-visible"
      style={{ width: "100%", height: "40px" }}
      viewBox="0 0 200 40"
      preserveAspectRatio="none"
    >
      <path
        d={`M ${from[0]} ${from[1]} Q ${(from[0] + to[0]) / 2} ${from[1] - 12}, ${to[0]} ${to[1]}`}
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={dashed ? "1 6" : "0"}
        fill="none"
      />
      {/* Endpoint dots */}
      <circle cx={from[0]} cy={from[1]} r="3" fill={color} />
      <circle cx={to[0]} cy={to[1]} r="3" fill={color} />
    </svg>
  );
}

// ─── Compass ────────────────────────────────────────────────
// Small compass rose. Size = 16 (inline) or 24 (icon).
export function Compass({ size = 20, color = "#22385A", className = "" }) {
  const r = size / 2;
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
    >
      <circle cx={r} cy={r} r={r - 1.5} fill="none" stroke={color} strokeWidth="1.5" />
      <path d={`M ${r} 2 L ${r + 2} ${r} L ${r} ${size - 2} L ${r - 2} ${r} Z`} fill={color} />
      <path d={`M 2 ${r} L ${r} ${r + 1.5} L ${size - 2} ${r} L ${r} ${r - 1.5} Z`} fill={color} opacity="0.35" />
      <circle cx={r} cy={r} r={1.5} fill={color} />
    </svg>
  );
}
